import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import { projectChatSettingsApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import type { AdminProject } from '../../types/admin';

interface ProjectChatSettingsDialogProps {
  project: AdminProject;
  onClose: () => void;
}

/** Dialog cấu hình thông báo Google Chat riêng theo 1 dự án (Admin only). */
export function ProjectChatSettingsDialog({ project, onClose }: ProjectChatSettingsDialogProps): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [notifyFeatureRequest, setNotifyFeatureRequest] = useState(false);
  const [notifyAcceptConfirmed, setNotifyAcceptConfirmed] = useState(false);
  const [notifyAcceptChangeRequested, setNotifyAcceptChangeRequested] = useState(false);
  const [notifyBugCreated, setNotifyBugCreated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    let active = true;
    projectChatSettingsApi
      .get(project.id)
      .then((s) => {
        if (!active) return;
        setWebhookUrl(s.webhook_url);
        setNotifyFeatureRequest(s.notify_feature_request === 1);
        setNotifyAcceptConfirmed(s.notify_accept_confirmed === 1);
        setNotifyAcceptChangeRequested(s.notify_accept_change_requested === 1);
        setNotifyBugCreated(s.notify_bug_created === 1);
      })
      .catch((err) => {
        if (active) notify(getApiErrorMessage(err, 'Không thể tải cấu hình thông báo'), 'error');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [project.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await projectChatSettingsApi.update(project.id, {
        webhook_url: webhookUrl,
        notify_feature_request: notifyFeatureRequest,
        notify_accept_confirmed: notifyAcceptConfirmed,
        notify_accept_change_requested: notifyAcceptChangeRequested,
        notify_bug_created: notifyBugCreated,
      });
      setWebhookUrl(updated.webhook_url);
      notify('Đã lưu cấu hình thông báo', 'success');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu cấu hình thông báo'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await projectChatSettingsApi.test(project.id);
      notify('Đã gửi tin nhắn thử', 'success');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Gửi thử thất bại'), 'error');
    } finally {
      setTesting(false);
    }
  };

  const webhookConfigured = webhookUrl.trim() !== '';

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Thông báo Google Chat: {project.name}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Webhook Google Chat"
                placeholder="https://chat.googleapis.com/..."
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                fullWidth
                size="small"
                disabled={saving}
              />
              <Tooltip title={webhookConfigured ? '' : 'Chưa cấu hình webhook'}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={() => void handleTest()}
                    disabled={testing || saving || !webhookConfigured}
                  >
                    {testing ? <CircularProgress size={20} /> : 'Gửi thử'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyFeatureRequest}
                  onChange={(e) => setNotifyFeatureRequest(e.target.checked)}
                  disabled={saving}
                />
              }
              label="Yêu cầu chức năng mới"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyAcceptConfirmed}
                  onChange={(e) => setNotifyAcceptConfirmed(e.target.checked)}
                  disabled={saving}
                />
              }
              label="Nghiệm thu — Đã xác nhận"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyAcceptChangeRequested}
                  onChange={(e) => setNotifyAcceptChangeRequested(e.target.checked)}
                  disabled={saving}
                />
              }
              label="Nghiệm thu — Yêu cầu thay đổi"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyBugCreated}
                  onChange={(e) => setNotifyBugCreated(e.target.checked)}
                  disabled={saving}
                />
              }
              label="Báo lỗi (bug mới)"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Đóng
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  );
}
